import React from 'react';

import Input from '../../../fields/Field';
import { DemarcheExtraField } from '../../types';
import {
  FieldLabel, FieldNote, FieldWrapper, GroupNote, GroupTitle, RadioChoice, RadioChoices, Select,
} from './styles';

const SELECT_PLACEHOLDER = 'Sélectionner…';

/**
 * The prototype marks every field required except free-text ones, which is encoded here as the
 * default. A config can override it explicitly once the backend owns the field definitions.
 */
const isRequired = (field: DemarcheExtraField): boolean => (
  field.required === undefined ? field.type !== 'text' : field.required
);

const labelText = (field: DemarcheExtraField): string => (
  isRequired(field) ? `${field.label}*` : field.label
);

type DynamicFieldProps = {
  field: DemarcheExtraField;
  value: string;
  showGroupTitle: boolean;
  errorMessage?: string;
  onChange: (id: string, value: string) => void;
};

const DynamicField: React.FC<DynamicFieldProps> = ({
  field, value, showGroupTitle, errorMessage, onChange,
}) => {
  const note = field.helpNote || field.optionalNote;

  const renderInput = (): JSX.Element => {
    switch (field.type) {
      case 'select':
        return (
          <>
            <FieldLabel as="label" htmlFor={field.id}>{labelText(field)}</FieldLabel>
            <Select
              id={field.id}
              name={field.id}
              value={value}
              onChange={(event) => onChange(field.id, event.target.value)}
            >
              <option value="">{SELECT_PLACEHOLDER}</option>
              {(field.options || []).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Select>
          </>
        );
      case 'radio':
        return (
          <>
            <FieldLabel id={`${field.id}-label`}>{labelText(field)}</FieldLabel>
            <RadioChoices role="radiogroup" aria-labelledby={`${field.id}-label`}>
              {(field.options || []).map((option) => (
                <RadioChoice key={option} isSelected={value === option}>
                  <input
                    type="radio"
                    name={field.id}
                    value={option}
                    checked={value === option}
                    onChange={() => onChange(field.id, option)}
                  />
                  <span>{option}</span>
                </RadioChoice>
              ))}
            </RadioChoices>
          </>
        );
      default:
        return (
          <Input
            id={field.id}
            label={labelText(field)}
            type={field.type === 'date' ? 'date' : 'text'}
            name={field.id}
            placeholder={field.placeholder || ''}
            value={value}
            onChange={(event) => onChange(field.id, event.target.value)}
            errorMessage={errorMessage || ''}
          />
        );
    }
  };

  return (
    <FieldWrapper data-test-selector={`dynamic-field-${field.id}`}>
      {showGroupTitle && field.group && <GroupTitle>{field.group}</GroupTitle>}
      {field.groupNote && <GroupNote>{field.groupNote}</GroupNote>}
      {renderInput()}
      {note && <FieldNote>{note}</FieldNote>}
    </FieldWrapper>
  );
};

export { DynamicField, isRequired };
